import { DeleteOutlined, PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import { App, Button, Form, Input, Space, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useQuery } from "@tanstack/react-query";
import { createRole, deleteRole, listRoles, type RoleItem } from "../../api/userAdminApi";
import { PageHeaderActions } from "../../shared/components/PageHeaderActions";
import { confirmDeletePreview } from "../../shared/components/ConfirmDeletePreview";
import { PersistedTable } from "../../shared/components/PersistedTable";
import { getApiErrorMessage } from "../../shared/utils/apiError";

export function RolesPage() {
  const { message } = App.useApp();
  const [form] = Form.useForm<{ roleName: string }>();
  const roles = useQuery({ queryKey: ["roles-page"], queryFn: listRoles });

  const columns: ColumnsType<RoleItem> = [
    { title: "#", width: 56, render: (_value, _row, index) => index + 1 },
    { title: "Role", dataIndex: "name", render: (value) => <Tag color={value === "Admin" ? "red" : value === "Accountant" ? "blue" : "default"}>{value}</Tag> },
    {
      title: "",
      width: 80,
      fixed: "right",
      render: (_, row) => (
        <Button
          size="small"
          danger
          icon={<DeleteOutlined />}
          disabled={["Admin", "Accountant", "Staff"].includes(row.name)}
          onClick={() => confirmDeletePreview({
            entityName: "Role",
            fields: [{ label: "Role", value: row.name }],
            onConfirm: async () => {
              try {
                await deleteRole(row.name);
                message.success("Role deleted");
                roles.refetch();
              } catch (err) {
                message.error(getApiErrorMessage(err, "Unable to delete role"));
              }
            }
          })}
        />
      )
    }
  ];

  return (
    <div>
      <PageHeaderActions
        title="Roles"
        subtitle="Dedicated role registry for user assignment. Built on the existing /roles API."
        right={<Button icon={<ReloadOutlined />} onClick={() => roles.refetch()} />}
      />
      <div className="role-create-bar">
        <Form
          form={form}
          layout="inline"
          onFinish={async (values) => {
            try {
              await createRole(values.roleName.trim());
              message.success("Role created");
              form.resetFields();
              roles.refetch();
            } catch (err) {
              message.error(getApiErrorMessage(err, "Unable to create role"));
            }
          }}
        >
          <Form.Item name="roleName" rules={[{ required: true, whitespace: true, message: "Role name is required" }]}>
            <Input placeholder="Role name" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" icon={<PlusOutlined />}>Create Role</Button>
          </Form.Item>
        </Form>
        <Space wrap>
          <Tag>Admin protected</Tag>
          <Tag>Accountant protected</Tag>
          <Tag>Staff protected</Tag>
        </Space>
      </div>
      <PersistedTable
        tableId="roles"
        rowKey={(row) => row.id || row.name}
        size="small"
        bordered
        loading={roles.isLoading}
        dataSource={roles.data ?? []}
        columns={columns}
        pagination={{ pageSize: 100 }}
      />
    </div>
  );
}
